import React, { FunctionComponent, ReactNode, useState } from 'react'
import { useIntl } from 'react-intl'

import styled, { useTheme } from 'styled-components/native'

import { ClientDetailsSidePanelTextTone, getClientDetailsSidePanelTextColor } from './ClientDetailsSidePanelText'
import { BodyText, Size as BodyTextSize } from '../../atoms/bodyText/BodyText'
import { ChevronIcon, ChevronIconDirection } from '../../atoms/icons/ChevronIcon'
import { PressableOpacity } from '../../atoms/pressableOpacity/PressableOpacity'
import { tID } from '../../utils/utils'

type ClientDetailsSidePanelCollapsibleHeaderBase = {
  kind: 'collapsible'
  collapsedTitle: ReactNode
  collapsedTitleIsLayout?: boolean
  expandedTitle?: ReactNode
  collapsedTitleTone?: ClientDetailsSidePanelTextTone
  expandedTitleTone?: ClientDetailsSidePanelTextTone
}

type ClientDetailsSidePanelControlledCollapsibleHeader = ClientDetailsSidePanelCollapsibleHeaderBase & {
  expanded: boolean
  onExpandedChange: (expanded: boolean) => void
  defaultExpanded?: never
}

type ClientDetailsSidePanelUncontrolledCollapsibleHeader = ClientDetailsSidePanelCollapsibleHeaderBase & {
  expanded?: never
  defaultExpanded?: boolean
  onExpandedChange?: (expanded: boolean) => void
}

export type ClientDetailsSidePanelCollapsibleHeader =
  | ClientDetailsSidePanelControlledCollapsibleHeader
  | ClientDetailsSidePanelUncontrolledCollapsibleHeader

export type ClientDetailsSidePanelStaticHeader = {
  kind: 'static'
  title?: ReactNode
  titleTone?: ClientDetailsSidePanelTextTone
}

export type ClientDetailsSidePanelSectionProps = {
  subtitle: ReactNode
  accessibilityName: string
  header: ClientDetailsSidePanelCollapsibleHeader | ClientDetailsSidePanelStaticHeader
  persistentContent?: ReactNode
  content?: ReactNode
  footer?: ReactNode
  testIDPrefix: string
}

const SectionContainer = styled.View(({ theme }) => ({
  width: '100%',
  padding: theme.spacing['24px'],
  backgroundColor: theme.colors.backgroundPrimary,
}))

const Header = styled.View({
  width: '100%',
})

const HeaderRow = styled.View({
  width: '100%',
  minHeight: 24,
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
})

const HeaderCopy = styled.View({
  flex: 1,
  minWidth: 0,
})

const CollapsedTitleContainer = styled.View({
  width: '100%',
})

const ChevronContainer = styled.View({
  width: 24,
  height: 24,
  alignItems: 'center',
  justifyContent: 'center',
})

const PersistentContent = styled.View(({ theme }) => ({
  width: '100%',
  marginTop: theme.spacing['16px'],
}))

const ExpandedContent = styled.View(({ theme }) => ({
  width: '100%',
  marginTop: theme.spacing['16px'],
}))

const Footer = styled.View(({ theme }) => ({
  width: '100%',
  marginTop: theme.spacing['24px'],
  alignItems: 'center',
}))

const hasRenderableContent = (content: ReactNode) => content !== undefined && content !== null && content !== false

export const ClientDetailsSidePanelSection: FunctionComponent<ClientDetailsSidePanelSectionProps> = ({
  subtitle,
  accessibilityName,
  header,
  persistentContent,
  content,
  footer,
  testIDPrefix,
}) => {
  const intl = useIntl()
  const theme = useTheme()
  const [internalExpanded, setInternalExpanded] = useState(
    header.kind === 'collapsible' ? (header.defaultExpanded ?? false) : true,
  )

  const isCollapsible = header.kind === 'collapsible'
  const isControlled = isCollapsible && header.expanded !== undefined
  const isExpanded = isCollapsible ? (isControlled ? header.expanded : internalExpanded) : true
  const contentID = `${testIDPrefix}-content`

  const subtitleText = (
    <BodyText
      testID={tID(`${testIDPrefix}-subtitle`)}
      size={BodyTextSize.CAPTION}
      color={theme.colors.textSecondary}
      text={subtitle}
      regular
    />
  )

  const hasPersistentContent = hasRenderableContent(persistentContent)
  const hasContent = hasRenderableContent(content)
  const hasFooter = hasRenderableContent(footer)
  const footerContent = hasFooter ? <Footer testID={tID(`${testIDPrefix}-footer`)}>{footer}</Footer> : null

  if (!isCollapsible) {
    return (
      <SectionContainer testID={tID(testIDPrefix)}>
        <Header>
          <HeaderRow>{subtitleText}</HeaderRow>
          {hasRenderableContent(header.title) && (
            <BodyText
              testID={tID(`${testIDPrefix}-static-title`)}
              size={BodyTextSize.DEFAULT}
              color={getClientDetailsSidePanelTextColor(header.titleTone, theme)}
              text={header.title}
              regular
            />
          )}
        </Header>
        {hasPersistentContent && (
          <PersistentContent testID={tID(`${testIDPrefix}-persistent-content`)}>{persistentContent}</PersistentContent>
        )}
        {(hasContent || hasFooter) && (
          <ExpandedContent
            nativeID={contentID}
            testID={tID(contentID)}
            style={!hasContent ? { marginTop: 0 } : undefined}
          >
            {content}
            {footerContent}
          </ExpandedContent>
        )}
      </SectionContainer>
    )
  }

  const accessibilityHint = isExpanded
    ? intl.formatMessage(
        {
          defaultMessage: 'Collapses {sectionName}',
          description: 'Accessibility hint for collapsing a client details side panel section',
        },
        { sectionName: accessibilityName },
      )
    : intl.formatMessage(
        {
          defaultMessage: 'Expands {sectionName}',
          description: 'Accessibility hint for expanding a client details side panel section',
        },
        { sectionName: accessibilityName },
      )

  const handlePress = () => {
    const nextExpanded = !isExpanded
    if (!isControlled) {
      setInternalExpanded(nextExpanded)
    }
    header.onExpandedChange?.(nextExpanded)
  }

  return (
    <SectionContainer testID={tID(testIDPrefix)}>
      <PressableOpacity
        testID={tID(`${testIDPrefix}-trigger`)}
        accessibilityHint={accessibilityHint}
        accessibilityExpanded={isExpanded}
        accessibilityState={{ expanded: isExpanded }}
        accessibilityControls={contentID}
        onPress={handlePress}
        style={{ width: '100%' }}
      >
        <Header>
          <HeaderRow>
            <HeaderCopy>{subtitleText}</HeaderCopy>
            <ChevronContainer
              accessible={false}
              accessibilityElementsHidden
              importantForAccessibility='no-hide-descendants'
            >
              <ChevronIcon
                size={24}
                direction={isExpanded ? ChevronIconDirection.UP : ChevronIconDirection.DOWN}
                fillColor={theme.colors.iconDefault}
              />
            </ChevronContainer>
          </HeaderRow>
          {!isExpanded &&
            (header.collapsedTitleIsLayout ? (
              <CollapsedTitleContainer testID={tID(`${testIDPrefix}-collapsed-title`)}>
                {header.collapsedTitle}
              </CollapsedTitleContainer>
            ) : (
              <BodyText
                testID={tID(`${testIDPrefix}-collapsed-title`)}
                size={BodyTextSize.DEFAULT}
                color={getClientDetailsSidePanelTextColor(header.collapsedTitleTone, theme)}
                text={header.collapsedTitle}
                regular
              />
            ))}
        </Header>
      </PressableOpacity>
      {hasPersistentContent && (
        <PersistentContent testID={tID(`${testIDPrefix}-persistent-content`)}>{persistentContent}</PersistentContent>
      )}
      {isExpanded && (
        <ExpandedContent nativeID={contentID} testID={tID(contentID)}>
          {hasRenderableContent(header.expandedTitle) && (
            <BodyText
              testID={tID(`${testIDPrefix}-expanded-title`)}
              size={BodyTextSize.DEFAULT}
              color={getClientDetailsSidePanelTextColor(header.expandedTitleTone, theme)}
              text={header.expandedTitle}
              regular
            />
          )}
          {content}
          {footerContent}
        </ExpandedContent>
      )}
    </SectionContainer>
  )
}
